import { evaluateAllIntegrationTests } from './integrationTestsService'

/**
 * Service to handle automated test evaluation for integrations
 */
export class AutomatedTestEvaluator {
  /**
   * Evaluates all tests for a GitHub integration
   * @param tenantId - The tenant ID
   * @param integrationData - GitHub integration data
   * @returns Promise with evaluation results
   */
  static async evaluateGitHubTests(
    tenantId: string,
    integrationData: {
      repositories?: any[]
      members?: any[]
      organizations?: any[]
      [key: string]: any
    }
  ): Promise<{ success: boolean; results?: any[]; error?: string }> {
    try {
      const result = await evaluateAllIntegrationTests('GitHub', tenantId, integrationData)
      
      if (result.success) {
        const passedTests = result.results.filter(r => r.status === 'Pass').length
        const totalTests = result.results.length
        
        
        return result
      } else {
        return result
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Evaluates all tests for any integration
   * @param integrationName - Name of the integration
   * @param tenantId - The tenant ID
   * @param integrationData - Integration data
   * @returns Promise with evaluation results
   */
  static async evaluateIntegrationTests(
    integrationName: string,
    tenantId: string,
    integrationData: any
  ): Promise<{ success: boolean; results?: any[]; error?: string }> {
    try {
      const result = await evaluateAllIntegrationTests(integrationName, tenantId, integrationData)
      
      if (result.success) {
        const passedTests = result.results.filter(r => r.status === 'Pass').length
        const totalTests = result.results.length
        
        return result
      } else {
        return result
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Schedules periodic evaluation of integration tests
   * @param integrationName - Name of the integration
   * @param tenantId - The tenant ID
   * @param intervalMinutes - Interval in minutes (default: 60)
   * @returns Function to stop the evaluation
   */
  static schedulePeriodicEvaluation(
    integrationName: string,
    tenantId: string,
    intervalMinutes: number = 60
  ): () => void {
    const interval = setInterval(async () => {
      try {
        // Fetch fresh integration data and evaluate tests
        // This would need to be implemented based on your data fetching logic
        console.log(`Scheduled evaluation for ${integrationName} at ${new Date().toISOString()}`)
        
        // For now, just log - you would implement actual data fetching here
        // const integrationData = await fetchIntegrationData(integrationName, tenantId)
        // await this.evaluateIntegrationTests(integrationName, tenantId, integrationData)
      } catch (error) {
        console.error(`Error in scheduled evaluation for ${integrationName}:`, error)
      }
    }, intervalMinutes * 60 * 1000)

    // Return function to stop the evaluation
    return () => clearInterval(interval)
  }
}

// Export utility functions
export const evaluateGitHubTests = AutomatedTestEvaluator.evaluateGitHubTests
export const evaluateIntegrationTests = AutomatedTestEvaluator.evaluateIntegrationTests
export const schedulePeriodicEvaluation = AutomatedTestEvaluator.schedulePeriodicEvaluation
